import type { BlueprintDocument, BlueprintListItem, DeploymentSummary, RunKind } from '../api/types';
import { strings } from '../strings';

const s = strings.blueprints;

/** The Deployments filter of the blueprints list (spec 6.2.2). */
export type DeploymentsFilter = 'any' | 'deployed' | 'notDeployed' | 'problems';

/** The largest number of deployments a blueprint summary holds (spec 4.4). */
export const SUMMARY_CAP = 10;

function hasProblems(deployment: DeploymentSummary) {
  return deployment.status === 'failed' || deployment.drift === 'detected';
}

/**
 * The word a deployment chip shows for a state that needs attention (spec 6.2.4): the active run's kind as a
 * verb (from the active runs, "running" while unknown), else failed, drift, new or destroyed; null when all is well.
 */
export function chipWord(
  deployment: DeploymentSummary,
  activeKinds: ReadonlyMap<string, RunKind>,
): string | null {
  if (deployment.activeRunId) {
    const kind = activeKinds.get(deployment.activeRunId);
    return kind ? s.chip.runKind[kind] : s.chip.running;
  }
  if (deployment.status === 'failed') return s.chip.failed;
  if (deployment.drift === 'detected') return s.chip.drift;
  if (deployment.status === 'new') return s.chip.new;
  if (deployment.status === 'destroyed') return s.chip.destroyed;
  return null;
}

/** How many versions the oldest applied deployment is behind the latest; 0 when none is. */
export function versionsBehind(blueprint: BlueprintListItem): number {
  const applied = blueprint.deployments
    .map((d) => d.currentBlueprintVersion)
    .filter((v): v is number => v !== null);
  return applied.length ? Math.max(0, blueprint.latestVersion - Math.min(...applied)) : 0;
}

/** The client-side filters over the loaded page (spec 6.2.2). */
export function matchesFilters(
  blueprint: BlueprintListItem,
  text: string,
  deployments: DeploymentsFilter,
  updatedBy: string,
): boolean {
  const words = text.toLocaleLowerCase().split(/\s+/).filter(Boolean);
  const haystack = `${blueprint.name} ${blueprint.description ?? ''}`.toLocaleLowerCase();
  if (!words.every((word) => haystack.includes(word))) return false;
  if (updatedBy && blueprint.updatedBy !== updatedBy) return false;
  switch (deployments) {
    case 'deployed':
      return blueprint.deployments.length > 0;
    case 'notDeployed':
      return blueprint.deployments.length === 0;
    case 'problems':
      return blueprint.deployments.some(hasProblems);
    default:
      return true;
  }
}

/** Parses an imported file: a JSON object with `schemaVersion` 1 and a `resources` array (spec 6.2.7). */
export function parseBlueprintFile(text: string): { document: BlueprintDocument } | { error: string } {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch {
    return { error: s.invalidJson };
  }
  if (
    typeof parsed !== 'object' ||
    parsed === null ||
    Array.isArray(parsed) ||
    (parsed as { schemaVersion?: unknown }).schemaVersion !== 1 ||
    !Array.isArray((parsed as { resources?: unknown }).resources)
  ) {
    return { error: s.notBlueprint };
  }
  return { document: parsed as BlueprintDocument };
}
